/**
 * Selectable option for a filter control.
 */
export type FilterOption = { value: string; label: string };

/**
 * Group of selectable options rendered under a section header (e.g. "My team"
 * vs "All") in a filter control.
 */
export type FilterOptionSection = { title: string; options: FilterOption[] };

/**
 * An entry in a filter control that is either a plain option or a titled
 * section of options.
 */
export type FilterOptionOrSection = FilterOption | FilterOptionSection;

/**
 * Facet-based filters applied to the entity results shown on the check and
 * track detail pages. `entityKinds` and `lifecycles` refer to the evaluated
 * entities.
 */
export type Filters = {
  entityKinds: string[];
  lifecycles: string[];
};

export const EMPTY_FILTERS: Filters = {
  entityKinds: [],
  lifecycles: [],
};

export const hasActiveFilters = (filters: Filters): boolean =>
  filters.entityKinds.length > 0 || filters.lifecycles.length > 0;

/**
 * Whether any entity-scoped selection is active on a details page: an owner
 * group, an entity kind or a lifecycle. Shared by the check and track detail
 * pages (e.g. to flag that the history chart doesn't reflect the filter).
 */
export const hasActiveEntitySelection = (
  filters: Filters,
  selectedGroups: string[],
): boolean => hasActiveFilters(filters) || selectedGroups.length > 0;

/**
 * Groups entity-owner options into a "My team" section (the current user's
 * teams) followed by an "All" section (every other owner). Team refs are
 * removed from the "All" section so option ids stay unique, and empty sections
 * are omitted. Within "All", currently selected owners are sorted to the top
 * (mirroring the insights owner picker).
 */
export const buildEntityOwnerSections = (
  teamOptions: FilterOption[],
  allOptions: FilterOption[],
  selectedRefs: string[] = [],
): FilterOptionOrSection[] => {
  const teamRefs = new Set(teamOptions.map(option => option.value));
  const selected = new Set(selectedRefs);
  const rest = allOptions
    .filter(option => !teamRefs.has(option.value))
    .sort((left, right) => {
      const leftSelected = selected.has(left.value) ? 0 : 1;
      const rightSelected = selected.has(right.value) ? 0 : 1;
      if (leftSelected !== rightSelected) {
        return leftSelected - rightSelected;
      }
      return left.label.localeCompare(right.label);
    });
  const sections: FilterOptionSection[] = [];
  if (teamOptions.length > 0) {
    sections.push({ title: 'My team', options: teamOptions });
  }
  if (rest.length > 0) {
    sections.push({ title: 'All', options: rest });
  }
  return sections;
};
